import {
  ScreeningHitQualification,
  ScreeningListSource,
  ScreeningNodeStatus,
  ScreeningResult,
  ScreeningSubject,
} from "./Screening";

function hashLabel(label: string): number {
  let hash = 0;
  for (const char of label) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 100;
  }
  return hash;
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function getListSource(hash: number): ScreeningListSource {
  switch (hash % 3) {
    case 0:
      return "SANCTIONS";
    case 1:
      return "PEP";
    default:
      return "ADVERSE_MEDIA";
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function resolveNodeStatus(hash: number): ScreeningNodeStatus {
  const bucket = hash % 10;
  if (bucket === 0) {
    return "INCONCLUSIVE";
  }
  if (bucket <= 2) {
    return "HIT";
  }
  return "CLEAR";
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function resolveMockQualification(
  status: ScreeningNodeStatus,
  hash: number,
): {
  qualification: ScreeningHitQualification;
  falsePositiveValidated?: boolean;
} {
  // An INCONCLUSIVE node has no confirmed match to qualify as true/false —
  // it can only ever be escalated as uncertain, never resolved to a verdict.
  if (status === "INCONCLUSIVE") {
    return { qualification: "UNCERTAIN" };
  }
  switch (hash % 3) {
    case 0:
      return { qualification: "TRUE_POSITIVE" };
    case 1:
      return { qualification: "FALSE_POSITIVE", falsePositiveValidated: true };
    default:
      return { qualification: "FALSE_POSITIVE", falsePositiveValidated: false };
  }
}

/**
 * Deterministic stand-in for the external RIC/VNeuron screening pipeline
 * (US-KYB-07-01) — no endpoint exists yet, see #25467. The real screening is
 * executed and qualified by compliance outside this onboarding flow; this
 * mock only projects a plausible already-resolved (or occasionally still
 * pending) result per subject so the flow can be exercised end to end.
 */
export function generateMockScreeningResults(
  subjects: ScreeningSubject[],
): ScreeningResult[] {
  return subjects.map((subject, index) => {
    const hash = hashLabel(subject.entityLabel);
    const status = resolveNodeStatus(hash);
    const id = `${subject.entityType}-${index}-${subject.entityLabel}`;

    if (status !== "HIT" && status !== "INCONCLUSIVE") {
      return {
        id,
        entityType: subject.entityType,
        entityLabel: subject.entityLabel,
        status,
      };
    }

    const { qualification, falsePositiveValidated } = resolveMockQualification(
      status,
      hash,
    );
    return {
      id,
      entityType: subject.entityType,
      entityLabel: subject.entityLabel,
      status,
      matchedName: subject.entityLabel,
      listSource: getListSource(hash),
      qualification,
      falsePositiveValidated,
    };
  });
}
